import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Activity, Boxes, Table2, Settings as SettingsIcon, Menu, X, LogOut, Plus } from "lucide-react";
import logo from "@/assets/catworth-logo.png";
import { useAuth } from "@/context/AuthContext";
import GlobalFilterBar from "@/components/GlobalFilterBar";
import CaptureModal from "@/components/CaptureModal";
import { Button } from "@/components/ui/button";
import { InstallAppButton, OfflineBanner } from "@/components/PwaControls";

const NAV = [
  { to: "/executive", label: "Executive", icon: LayoutDashboard, perm: "nesting.read" },
  { to: "/operations", label: "Operations", icon: Activity, perm: "nesting.read" },
  { to: "/inventory", label: "Inventory", icon: Boxes, perm: "inventory.read" },
  { to: "/records", label: "Nesting Records", icon: Table2, perm: "nesting.read" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, perm: "master.read" },
];

export default function Layout({ children, title, subtitle, showFilters = true, meta }) {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [capture, setCapture] = useState(false);

  const items = NAV.filter((n) => can(n.perm));

  const SidebarInner = (
    <div className="flex flex-col h-full" style={{ background: "#0F172A" }}>
      <div className="px-5 py-5 border-b" style={{ borderColor: "#1E293B" }}>
        <div className="bg-white rounded-md px-3 py-2 flex items-center justify-center">
          <img src={logo} alt="Catworth Laser" className="h-9 object-contain" data-testid="brand-logo" />
        </div>
        <p className="text-[11px] tracking-widest mt-3 font-semibold" style={{ color: "#8B5CF6" }}>
          LASER OPERATIONS INTELLIGENCE
        </p>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {items.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            onClick={() => setOpen(false)}
            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
            data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <n.icon size={18} strokeWidth={2} />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-4 py-4 border-t" style={{ borderColor: "#1E293B" }}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-sm"
               style={{ background: "#7C3AED" }}>
            {(user?.displayName || "U").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-white truncate" data-testid="user-name">{user?.displayName}</p>
            <p className="text-[11px] truncate" style={{ color: "#94A3B8" }}>{user?.roleLabel}</p>
          </div>
        </div>
        <button onClick={logout} data-testid="logout-btn"
          className="w-full flex items-center justify-center gap-2 text-sm py-2 rounded-md text-slate-300 hover:text-white transition-colors"
          style={{ background: "#1E293B" }}>
          <LogOut size={15} /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex" style={{ background: "#F1F5F9" }}>
      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-64 shrink-0 fixed inset-y-0 left-0 z-30">{SidebarInner}</aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 h-full">{SidebarInner}</div>
          <div className="flex-1 bg-black/50" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b" style={{ borderColor: "#E2E8F0" }}>
          <OfflineBanner />
          <div className="px-4 sm:px-6 py-3 flex items-center gap-3">
            <button className="lg:hidden p-2 -ml-2" onClick={() => setOpen(true)} data-testid="open-menu-btn">
              <Menu size={22} />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="font-head text-lg sm:text-xl font-semibold tracking-tight truncate" data-testid="page-title">{title}</h1>
              {subtitle && <p className="text-xs text-slate-500 truncate">{subtitle}</p>}
            </div>
            <InstallAppButton />
            {can("nesting.create") && (
              <Button onClick={() => setCapture(true)} data-testid="new-record-btn"
                className="text-white font-semibold" style={{ background: "#0D9488" }}>
                <Plus size={17} className="mr-1" /> <span className="hidden sm:inline">New Record</span>
              </Button>
            )}
          </div>
          {showFilters && <GlobalFilterBar meta={meta} />}
        </header>

        <main className="flex-1 px-4 sm:px-6 py-5">{children}</main>
      </div>

      {capture && <CaptureModal open={capture} onClose={() => setCapture(false)} />}
    </div>
  );
}
